import React, { useState, useEffect } from 'react';
import { Check, X, Eye, EyeOff, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const PasswordStrength = ({ 
  password, 
  username, 
  email, 
  onValidationChange,
  showRequirements = true,
  className = ""
}) => {
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { backendUrl, token } = useAuth();

  useEffect(() => {
    if (password && password.length > 0) {
      validatePassword(password);
    } else {
      setStrength(null);
      onValidationChange?.(false, []);
    }
  }, [password, username, email]);

  const validatePassword = async (pwd) => {
    if (!pwd || pwd.length === 0) return;

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/password/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          password: pwd,
          username: username,
          email: email
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStrength(data);
        onValidationChange?.(data.pci_dss_compliant, data.suggestions);
      } else {
        console.error('Failed to validate password');
        setStrength(null);
        onValidationChange?.(false, ['Password validation failed']);
      }
    } catch (error) {
      console.error('Error validating password:', error);
      setStrength(null);
      onValidationChange?.(false, ['Network error during validation']);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-green-600 bg-green-100';
      case 'Strong': return 'text-blue-600 bg-blue-100';
      case 'Good': return 'text-yellow-600 bg-yellow-100';
      case 'Weak': return 'text-orange-600 bg-orange-100';
      case 'Very Weak': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getProgressColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-blue-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Password Input with Show/Hide */}
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          readOnly
          className="hidden" // Hidden since this is just for strength checking
        />
      </div>

      {/* Strength Indicator */}
      {password && (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm">Validating password...</span>
            </div>
          ) : strength ? (
            <div className="space-y-3">
              {/* Strength Level */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Password Strength</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStrengthColor(strength.level)}`}>
                  {strength.level} ({strength.score}/100)
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(strength.score)}`}
                  style={{ width: `${strength.score}%` }}
                ></div>
              </div>

              {/* PCI DSS Compliance */}
              <div className="flex items-center gap-2">
                {strength.pci_dss_compliant ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <Check className="h-4 w-4" />
                    <span className="text-sm font-medium">PCI DSS Compliant</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-600">
                    <X className="h-4 w-4" />
                    <span className="text-sm font-medium">Not PCI DSS Compliant</span>
                  </div>
                )}
              </div>

              {/* Suggestions */}
              {strength.suggestions && strength.suggestions.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-amber-800">Suggestions:</p>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {strength.suggestions.map((suggestion, index) => (
                          <li key={index} className="flex items-start gap-1">
                            <span className="text-amber-600 mt-1">•</span>
                            <span>{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Password Requirements */}
      {showRequirements && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-blue-900">PCI DSS Password Requirements</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Minimum 12 characters long
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Must contain uppercase letters (A-Z)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Must contain lowercase letters (a-z)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Must contain numbers (0-9)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Must contain special characters (!@#$%^&*)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Cannot contain username or email information
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                  Cannot be a common or easily guessable password
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Password Input Component with Strength Validation
const PasswordInput = ({ 
  value, 
  onChange, 
  username, 
  email, 
  placeholder = "Enter password",
  label = "Password",
  required = true,
  className = "",
  onValidationChange,
  showRequirements = true,
  name = "password"
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          autoComplete="new-password"
          name={name}
          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>

      <PasswordStrength
        password={value}
        username={username}
        email={email}
        onValidationChange={onValidationChange}
        showRequirements={showRequirements}
      />
    </div>
  );
};

export { PasswordStrength, PasswordInput };